import React, { useState, useEffect } from 'react';
import {
  Button, Card, Col, Row, notification, Space, Popconfirm, message, Empty,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ArrowLeftOutlined } from '@ant-design/icons';
import SubjectModal from './addSubject';
import {
  ADD_SUBJECT, GET_SINGLE_COHORT, DELETE_SUBJECT, UPDATE_SUBJECT,
} from '../../../graphql';
import ViewSpinner from '../../../components/viewSpinner/ViewSpinner';
import './styles.less';

export default () => {
  const { id: cohortId } = useParams();
  const [addSubject, { loading: addLoading, data: subjectPayload }] = useMutation(ADD_SUBJECT);
  const [deleteSubject] = useMutation(DELETE_SUBJECT);
  const [updateSubject] = useMutation(UPDATE_SUBJECT);
  const { data: cohortRes, loading: cohortLoading } = useQuery(GET_SINGLE_COHORT, {
    variables: { id: cohortId },
  });
  const [modelaConfig, setModalConfig] = useState({ mode: 'ADD' });
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [cohort, setCohort] = useState(null);
  const [subjects, setSubject] = useState([]);
  const navigate = useNavigate();
  const handleAddClick = () => {
    setModalConfig({ mode: 'ADD' });
    setShowSubjectModal(true);
  };

  const handleEditClick = (id, name) => {
    setModalConfig({ mode: 'EDIT', id, name });
    setShowSubjectModal(true);
  };
  /**
   * The cohort's subject list is read back from the cache after each change
   * rather than patched through nested connect/disconnect payloads.
   */
  const writeSubjects = (proxy, update) => {
    const variables = { id: cohortId };
    const cached = proxy.readQuery({ query: GET_SINGLE_COHORT, variables });
    if (!cached) return;
    proxy.writeQuery({
      query: GET_SINGLE_COHORT,
      variables,
      data: { cohort: { ...cached.cohort, subjects: update(cached.cohort.subjects) } },
    });
  };

  const handleModalSubmit = async (name, config) => {
    try {
      if (config.mode === 'ADD') {
        // `createdBy` comes from the token.
        await addSubject({
          variables: { input: { name, cohortId } },
          update: (proxy, { data: { createSubject: created } }) => {
            writeSubjects(proxy, (existing) => [...existing, created]);
          },
        });
        return;
      }

      const { id } = config;
      await updateSubject({
        variables: { id, input: { name } },
        update: (proxy, { data: { updateSubject: updated } }) => {
          writeSubjects(proxy, (existing) => existing.map(
            (subject) => (subject.id === updated.id ? updated : subject),
          ));
        },
      });
      setShowSubjectModal(false);
    } catch (error) {
      notification.error({ message: error.message || 'Could not add subject' });
    }
  };

  useEffect(() => {
    if (!addLoading && subjectPayload) {
      notification.success({ message: 'New Subject Added', description: "You've successfully created a new subject" });
      setShowSubjectModal(false);
    }
  }, [addLoading, subjectPayload]);

  useEffect(() => {
    if (!cohortLoading && cohortRes) {
      const { subjects: cohortSubjects, ...rest } = cohortRes.cohort;
      setCohort(rest);
      setSubject([...cohortSubjects].reverse());
    }
  }, [cohortRes, cohortLoading]);

  const handleSubjectDelete = async (subjectId) => {
    try {
      await deleteSubject({
        variables: { id: subjectId },
        update: (proxy) => {
          writeSubjects(proxy, (existing) => existing.filter(({ id }) => id !== subjectId));
        },
      });
      message.success('Subject deleted');
    } catch (error) {
      message.error(error.message || 'Something went wrong');
    }
  };

  return (
    <div>
      {!cohortLoading && (
      <Row>
        <SubjectModal
          loading={addLoading}
          handleCancel={() => setShowSubjectModal(false)}
          handleSubmit={handleModalSubmit}
          visible={showSubjectModal}
          config={modelaConfig}
        />
        <Col span={24}>
          <Card className="gx-card gx-mb-3">
            <div className="cohort-detail">
              {cohort && (
              <>
                <div>
                  <h4>{cohort.name}</h4>
                  <small>{`${subjects.length} subject, created ${new Date(cohort.createdAt).toLocaleDateString()}`}</small>
                </div>
                <Button onClick={() => navigate(-1)} size="middle" shape="circle" icon={<ArrowLeftOutlined />} />
              </>
              )}
            </div>
          </Card>
        </Col>
        <Col span={24}>
          <Card className="gx-card gx-pb-0">
            <div className="subject-header">
              <h5>Subjects</h5>
              <Button onClick={handleAddClick} type="primary">Add</Button>
            </div>
            {
              subjects.map(({ id: subjectId, name }) => (
                <div key={subjectId} className="subject-item">
                  <h5>{name}</h5>
                  <Space size="small">
                    <Button onClick={() => handleEditClick(subjectId, name)} type="text">
                      Edit
                    </Button>
                    <Button danger type="text">
                      <Popconfirm
                        title="Are you sure want to delete"
                        onConfirm={() => handleSubjectDelete(subjectId)}
                        onCancel={() => {}}
                        okText="Delete"
                        cancelText="Cancel"
                        placement="topLeft"
                      >
                        Delete
                      </Popconfirm>
                    </Button>
                  </Space>
                </div>
              ))
            }
            {!cohortLoading && subjects.length === 0 && <Empty description="No subjects yet" />}
          </Card>
        </Col>
      </Row>
      )}
      {cohortLoading && <ViewSpinner />}
    </div>
  );
};
